import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { DjangoWaffleType } from "./django-waffle-type";

// allow window.waffle to hydrate us
declare global {
  interface Window {
    waffle: Partial<DjangoWaffleType>;
  }
}

// fallback until the real data arrives
const DEFAULT_WAFFLE: DjangoWaffleType = {
  FLAGS: {},
  SWITCHES: {},
  SAMPLES: {},
  FLAG_VENUE_IDS: {},
};

const WaffleContext = createContext<DjangoWaffleType>(DEFAULT_WAFFLE);

export const WaffleProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [waffle, setWaffle] = useState<DjangoWaffleType>(DEFAULT_WAFFLE);

  useEffect(() => {
    if (typeof window !== "undefined" && window.waffle) {
      setWaffle({
        FLAGS: window.waffle.FLAGS ?? {},
        SWITCHES: window.waffle.SWITCHES ?? {},
        SAMPLES: window.waffle.SAMPLES ?? {},
        FLAG_VENUE_IDS: window.waffle.FLAG_VENUE_IDS ?? {},
      });
    }
  }, []);

  return (
    <WaffleContext.Provider value={waffle}>{children}</WaffleContext.Provider>
  );
};

export type WaffleContextValue = DjangoWaffleType & {
  /**
   * Determine if a feature flag is active, with venue overrides taking priority:
   * 1. If the flag is globally enabled → returns true.
   * 2. If the flag is globally off but `venueId` is in `FLAG_VENUE_IDS[flagName]` → returns true.
   * 3. Otherwise, returns false.
   */
  isFlagActive: (flagName: string, venueId?: number) => boolean;
};

export const checkFlagActive = (
  waffle: DjangoWaffleType,
  flagName: string,
  venueId?: number
) => {
  const isGloballyEnabled = !!waffle.FLAGS[flagName];
  const scopedVenues = waffle.FLAG_VENUE_IDS?.[flagName] || [];

  // 1. Globally enabled → always active
  if (isGloballyEnabled) {
    return true;
  }

  // 2. If not globally enabled, check venue override
  if (venueId !== undefined && scopedVenues.includes(venueId)) {
    return true;
  }

  // 3. Otherwise, it's off
  return false;
};

/**
 * React hook to read waffle data and do venue-aware flag checks.
 * Must be used under <WaffleProvider>.
 */
export function useWaffle(): WaffleContextValue {
  const waffle = useContext(WaffleContext);
  if (!waffle) {
    throw new Error("useWaffle must be used within a WaffleProvider");
  }

  const isFlagActive = useCallback(
    (flagName: string, venueId?: number): boolean => {
      return checkFlagActive(waffle, flagName, venueId);
    },
    [waffle]
  );

  return { ...waffle, isFlagActive };
}
